import { ProductLine } from '@kouto/types';
import { useAppState } from 'AppProvider';
import Hero from 'components/Hero/Hero';
import { NavigationHeader } from 'components/NavigationHeader';
import { Wrapper } from 'components/theme/Wrapper';
import React from 'react';
import { ReducerState } from 'types/reducer';

import { useLandingPageRouteParams } from 'hooks/useLandingPageRouteParams';
import useScheduleViewConfig from 'hooks/useScheduleViewConfig';
import { PageTitle } from 'components/atoms/PageTitle';
import { SrOnly } from 'components/common/styled/common-styled';
import { ScheduleViewTabs } from 'components/molecules/ScheduleViewTabs';
import LandingPageHeader from './components/LandingPageHeader';
import { LandingPageHero } from './components/LandingPageHero/LandingPageHero';
import ListingsResults from './components/ListingsResults';
import useProductLandingListings from './hooks/useProductLandingListings';
import useResultsAnnouncement from './hooks/useResultsAnnouncement';

interface ProductLandingPageProps {
  product: ProductLine;
}

export const ProductLandingPage = ({ product }: ProductLandingPageProps) => {
  const { isResults, product: productRoute } = useLandingPageRouteParams();
  const { isError, isLoading, listings, loadMore, pageTitle } =
    useProductLandingListings(product);
  const { showScheduleView, showTabs } = useScheduleViewConfig();
  const settings = useAppState((state: ReducerState) => state.brand.settings);
  const showBrowseTabs = showScheduleView && showTabs;

  const resultsAnnouncement = useResultsAnnouncement({
    count: listings.length,
    isLoading,
    isResults: Boolean(isResults),
  });

  return (
    <>
      <PageTitle title={pageTitle} />
      <SrOnly as="div" aria-live="polite" aria-atomic="true">
        {resultsAnnouncement}
      </SrOnly>
      {/* Schedule view lifts the brand Hero above the wrapper on the catalog surface; the
          original layout never had a Hero here. */}
      {showScheduleView && !isResults && <Hero settings={settings} />}
      <Wrapper
        style={{
          position: 'relative',
          zIndex: 5,
        }}
      >
        {(isResults || productRoute) && (
          <NavigationHeader
            hideBackButton={isResults && showScheduleView ? true : undefined}
            centerContent={
              isResults && showBrowseTabs ? (
                <ScheduleViewTabs active="browse" />
              ) : undefined
            }
          />
        )}
        {!isResults && (
          <LandingPageHero
            isLoading={isLoading}
            listings={listings}
            product={product}
          />
        )}
        {showBrowseTabs && !isResults && <ScheduleViewTabs active="browse" />}
        <LandingPageHeader
          product={product}
          isScheduleView={showScheduleView}
        />
        <ListingsResults
          isError={isError}
          isLoading={isLoading}
          isResults={Boolean(isResults)}
          listings={listings}
          loadMore={loadMore}
        />
      </Wrapper>
    </>
  );
};
